/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        API Key Management Zod Schemas & DTOs
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

export const ALLOWED_API_SCOPES = [
  '*',
  'text:write',
  'text:read',
  'chat:write',
  'chat:read',
  'images:write',
  'images:read',
  'audio:write',
  'audio:read',
  'translations:write',
  'translations:read',
] as const;

export type ApiScope = (typeof ALLOWED_API_SCOPES)[number];

/**
 * Request schema for creating a new developer API key.
 */
export const CreateApiKeySchema = z
  .object({
    name: z.string().trim().min(1, 'Key name is required').max(100).openapi({
      description: 'Human-readable friendly key name',
      example: 'Production AI Generation Bot',
    }),
    scopes: z
      .array(z.string())
      .default(['text:write', 'chat:write'])
      .openapi({
        description: 'Granular API permission scopes granted to this key',
        example: ['text:write', 'chat:write', 'images:write'],
      }),
    rateLimitRpm: z
      .number()
      .int()
      .min(1)
      .max(1000)
      .default(60)
      .openapi({
        description: 'Requests per minute allowed for this key (Sliding Window)',
        example: 60,
      }),
    monthlySpendLimit: z
      .number()
      .positive()
      .nullable()
      .optional()
      .openapi({
        description: 'Optional monthly spending threshold in credits or USD',
        example: 100000,
      }),
    environment: z
      .enum(['live', 'test'])
      .default('live')
      .openapi({
        description: 'Key environment mode',
        example: 'live',
      }),
  })
  .openapi('CreateApiKeyRequest');

export type CreateApiKeyDto = z.infer<typeof CreateApiKeySchema>;

/**
 * Request schema for updating an existing developer API key.
 */
export const UpdateApiKeySchema = z
  .object({
    name: z.string().trim().min(1).max(100).optional().openapi({
      description: 'Updated friendly name',
      example: 'Staging Integration Bot',
    }),
    scopes: z.array(z.string()).optional().openapi({
      description: 'Updated scopes array',
      example: ['text:read', 'chat:read'],
    }),
    rateLimitRpm: z.number().int().min(1).max(1000).optional().openapi({
      description: 'Updated rate limit RPM',
      example: 120,
    }),
    monthlySpendLimit: z.number().positive().nullable().optional().openapi({
      description: 'Updated monthly spend limit',
      example: 50000,
    }),
    isActive: z.boolean().optional().openapi({
      description: 'Active status toggle',
      example: true,
    }),
  })
  .openapi('UpdateApiKeyRequest');

export type UpdateApiKeyDto = z.infer<typeof UpdateApiKeySchema>;

/**
 * Schema for single sanitized API Key representation.
 */
export const ApiKeyItemSchema = z
  .object({
    id: z.string().openapi({ description: 'API Key unique ID', example: 'key_01HXYZ123' }),
    name: z.string().openapi({ description: 'Key name', example: 'Production Bot' }),
    keyPrefix: z.string().openapi({ description: 'Key prefix (e.g., sk_live_)', example: 'sk_live_' }),
    keyHint: z.string().openapi({ description: 'Last 4 characters of key for recognition', example: 'a1b2' }),
    scopes: z.array(z.string()).openapi({ description: 'Allowed scopes', example: ['text:write'] }),
    rateLimitRpm: z.number().openapi({ description: 'Rate limit RPM', example: 60 }),
    monthlySpendLimit: z.number().nullable().openapi({ description: 'Monthly limit', example: null }),
    isActive: z.boolean().openapi({ description: 'Is key enabled', example: true }),
    lastUsedAt: z.number().nullable().openapi({ description: 'Unix timestamp of last usage', example: 1729340000 }),
    createdAt: z.number().openapi({ description: 'Unix timestamp of creation', example: 1729300000 }),
  })
  .openapi('ApiKeyItem');

export type ApiKeyItemDto = z.infer<typeof ApiKeyItemSchema>;

/**
 * Schema for API key creation response containing the plaintext secret.
 */
export const ApiKeyCreatedSchema = z
  .object({
    apiKey: ApiKeyItemSchema,
    secretKey: z.string().openapi({
      description: 'Plaintext secret API key shown only once upon creation',
      example: 'sk_live_1234567890abcdef1234567890abcdef1234567890abcdef',
    }),
  })
  .openapi('ApiKeyCreatedResponse');

export type ApiKeyCreatedDto = z.infer<typeof ApiKeyCreatedSchema>;

/**
 * Cached API Key representation in Cloudflare KV (`SYSTEM_KEYS_KV`).
 */
export interface CachedApiKeyMeta {
  keyId: string;
  workspaceId: string;
  userId: string;
  scopes: string[];
  rateLimitRpm: number;
  monthlySpendLimit: number | null;
  isActive: boolean;
}

